import {
  CalendarIcon,
  Crown,
  Expand,
  Gem,
  LogOutIcon,
  PlusIcon,
  SettingsIcon,
  ShieldCheckIcon,
  Sparkles,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { useEffect, useRef, useState } from 'react';

import { EXAMPLE_GROUP_ID, groupPro, groupProEntry } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { GroupWithDashboard } from '@splitfiesta/shared';
import { hasAnyEditPermission } from '@splitfiesta/shared';
import { Badge, Button } from '@splitfiesta/ui';

import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { PreloadLink } from '@/components/common/PreloadLink';
import { ProGroupDialogContainer } from '@/components/pro/ProGroupDialogContainer';
import { ProPill } from '@/components/pro/ProPill';
import { BackButton } from '@/components/ui/BackButton';
import { LinkButton } from '@/components/ui/LinkButton';
import { RelativeTime } from '@/components/ui/RelativeTime';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import {
  useCommonShortcuts,
  useKeyboardShortcut,
} from '@/hooks/useKeyboardShortcut';
import { useLeaveGroup } from '@/hooks/useLeaveGroup';

import { useAuthStore } from '@/stores/authStore';

import { focalObjectPosition } from '@/utils/focalPoint';

interface GroupHeaderProps {
  group: GroupWithDashboard;
}

export function GroupHeader({ group }: GroupHeaderProps) {
  const { t, i18n } = useTranslation();
  const proEnabled = useFeatureFlag('pro');
  const user = useAuthStore(state => state.user);
  const navigate = useNavigate();
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [isProDialogOpen, setIsProDialogOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const proActive = groupPro(group).active;
  // The control that opened the dialog unmounts once a change lands, which
  // drops keyboard focus to <body>; the one that replaced it takes it instead.
  // Armed only by a change made here, so a flip from elsewhere (a supporter
  // lapsing, a background refetch) never pulls focus into the header.
  const proActiveBeforeChange = useRef<boolean | null>(null);
  const openPro = () => {
    proActiveBeforeChange.current = null;
    setIsProDialogOpen(true);
  };
  useEffect(() => {
    const before = proActiveBeforeChange.current;
    if (isProDialogOpen || before === null || before === proActive) return;
    proActiveBeforeChange.current = null;
    requestAnimationFrame(() => {
      const targets =
        headerRef.current?.querySelectorAll<HTMLElement>('[data-pro-focus]') ??
        [];
      // Each layout renders its own copy; the hidden ones refuse focus.
      for (const target of targets) {
        target.focus();
        if (document.activeElement === target) return;
      }
    });
  }, [isProDialogOpen, proActive]);

  const membersLabel = t('groups:stats.members', {
    count: group.balances?.length || group.memberCount || 0,
  });

  // Format date for display
  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year:
        date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
  };

  // Get formatted date range for display
  const getDateDisplay = () => {
    const { startDate, endDate } = group.categorySettings || {};
    if (!startDate && !endDate) return null;

    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    if (startDate) {
      return formatDate(startDate);
    }
    return null;
  };

  const dateDisplay = getDateDisplay();

  // Get keyboard shortcut info
  const { modifierKey, modifierSymbol } = useCommonShortcuts();

  // Leave group hook
  const { leaveGroup, isLeaving } = useLeaveGroup({
    groupId: group.id,
    groupName: group.name,
  });

  // Register keyboard shortcut (Cmd+E or Ctrl+E) for adding expense
  useKeyboardShortcut(
    () => {
      navigate(`/groups/${group.id}/expenses/create`);
    },
    {
      key: 'e',
      modifiers: [modifierKey],
      enabled: true,
    }
  );

  // Show Settings/Edit entry to owners, admins, and members who can edit at least one field.
  const canAccessSettings =
    group.userRole === 'owner' ||
    group.userRole === 'admin' ||
    hasAnyEditPermission(group.userRole ?? 'member', group.memberPermissions);

  // The client-side example group ("Weekend in Barcelona") wears an
  // "Example" badge in every hero layout (image/no-image × mobile/desktop)
  // so nobody mistakes its people and expenses for real.
  const exampleBadge =
    group.id === EXAMPLE_GROUP_ID ? (
      <Badge
        variant="warning"
        size="sm"
        className="shrink-0 bg-black/70 dark:bg-black/70 text-orange-300 dark:text-orange-300 backdrop-blur-sm"
      >
        <Sparkles className="h-3 w-3 mr-1" />
        {t('groups:exampleGroup.tag')}
      </Badge>
    ) : null;

  const {
    available: proAvailable,
    isPro: showProPill,
    canMakePro: showProAction,
  } = groupProEntry({ proEnabled, user, group });
  const proPill = showProPill ? <ProPill onClick={openPro} /> : null;
  const heroProPill = showProPill ? (
    <ProPill
      onClick={openPro}
      className="bg-black/70 dark:bg-black/70 text-orange-300 dark:text-orange-300 backdrop-blur-sm"
    />
  ) : null;

  const proAction = (testId: string, className?: string) =>
    showProAction ? (
      <Button
        variant="ghost"
        size="sm"
        onClick={openPro}
        data-testid={testId}
        data-pro-focus=""
        aria-label={t('groups:pro.actionA11y')}
        className={
          className ??
          'text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10 whitespace-nowrap'
        }
      >
        <Gem aria-hidden="true" className="w-4 h-4 mr-1" />
        {t('groups:pro.action')}
      </Button>
    ) : null;
  const proDialog = proAvailable ? (
    <ProGroupDialogContainer
      isOpen={isProDialogOpen}
      onClose={() => setIsProDialogOpen(false)}
      onChanged={() => {
        proActiveBeforeChange.current = proActive;
      }}
      group={group}
    />
  ) : null;

  // Hero banner when group has an image
  if (group.image) {
    return (
      <div ref={headerRef} className="relative" data-testid="group-info">
        {proDialog}
        {/* Image Viewer Modal - shows original full-size image */}
        <ImageViewerModal
          isOpen={isImageViewerOpen}
          onClose={() => setIsImageViewerOpen(false)}
          imageUrl={group.imageOriginal || group.image}
          title={group.name}
          downloadFilename={`${group.name.replace(/[^a-zA-Z0-9]/g, '-')}-cover`}
        />

        {/* Hero Banner Container */}
        <div className="relative h-[180px] sm:h-[200px] w-full overflow-hidden rounded-xl">
          {/* Background Image - cover-fit and anchored at the chosen focal point */}
          <img
            src={group.image}
            alt=""
            data-testid="group-hero-image"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: focalObjectPosition(group.imageFocalY) }}
            aria-hidden="true"
          />
          {/* Expand/View image button - mobile: bottom-right, desktop: bottom-left */}
          <button
            type="button"
            onClick={() => setIsImageViewerOpen(true)}
            className="absolute bottom-3 right-3 sm:bottom-14 sm:right-auto sm:left-6 z-30 p-1.5 rounded-full bg-black/70 hover:bg-black/80 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500"
            aria-label={t('common:imageViewer.tapToView')}
            title={t('common:imageViewer.tapToView')}
          >
            <Expand className="w-3.5 h-3.5" />
          </button>

          {/* Mobile: Back button + Title + Badges at top */}
          <div className="sm:hidden absolute top-0 left-0 right-0 p-4 z-20">
            <div className="flex items-start gap-3">
              <BackButton
                variant="ghost"
                size="sm"
                className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white shrink-0"
                fallbackUrl="/groups"
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                  {/* line-clamp forces overflow:hidden, which clips the
                      caption shadow at the box edge — p-4 gives it bleed
                      room, -m-4 cancels the layout shift. */}
                  <h1 className="text-xl font-bold text-white line-clamp-3 caption-shadow p-4 -m-4">
                    {group.name}
                  </h1>
                  {exampleBadge}
                  {heroProPill}
                  {group.currency && (
                    <Badge
                      variant="info"
                      size="sm"
                      className="shrink-0 bg-black/70 dark:bg-black/70 text-white dark:text-white border-white/30 backdrop-blur-sm"
                    >
                      {group.currency}
                    </Badge>
                  )}
                  {group.userRole === 'owner' && (
                    <Badge
                      variant="warning"
                      size="sm"
                      className="shrink-0 bg-black/70 dark:bg-black/70 text-amber-300 dark:text-amber-300 backdrop-blur-sm"
                      aria-label="Owner"
                    >
                      <Crown className="h-3 w-3 mr-1" />
                      {t('members:roles.owner')}
                    </Badge>
                  )}
                  {group.userRole === 'admin' && (
                    <Badge
                      variant="info"
                      size="sm"
                      className="shrink-0 bg-black/70 dark:bg-black/70 text-blue-300 dark:text-blue-300 backdrop-blur-sm"
                      aria-label="Admin"
                    >
                      <ShieldCheckIcon className="h-3 w-3 mr-1" />
                      {t('members:roles.admin')}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: Stats at bottom of hero */}
          <div className="sm:hidden absolute bottom-0 left-0 right-0 px-4 pb-4 z-10">
            <div className="flex flex-wrap items-center gap-2 text-sm text-white caption-shadow">
              {dateDisplay ? (
                <>
                  <span className="flex items-center gap-1">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {dateDisplay}
                  </span>
                  <span>•</span>
                </>
              ) : (
                <>
                  <span>
                    {t('groups:stats.createdPrefix')}{' '}
                    <RelativeTime date={group.createdAt} />
                  </span>
                  <span>•</span>
                </>
              )}
              <Link
                to={`/groups/${group.id}/members`}
                className="hover:underline"
              >
                {membersLabel}
              </Link>
            </div>
          </div>

          {/* Desktop: Back Button + Title at top, Badges + Settings at top right */}
          <div className="hidden sm:flex absolute top-0 left-0 right-0 p-6 justify-between items-start z-20">
            <div className="flex items-start gap-3 flex-1 min-w-0 pr-4">
              <BackButton
                variant="ghost"
                size="sm"
                className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white shrink-0"
                fallbackUrl="/groups"
              />
              {/* line-clamp forces overflow:hidden, which clips the caption
                  shadow — p-4 gives it bleed room, negative margins cancel
                  it (-mt-3.5, not -mt-4, keeps the old pt-0.5 nudge that
                  aligns the title with the back button). */}
              <h1 className="text-2xl font-bold text-white line-clamp-3 caption-shadow p-4 -mx-4 -mb-4 -mt-3.5">
                {group.name}
              </h1>
            </div>
            <div
              className={`flex flex-col items-end gap-2 shrink-0${showProPill ? ' max-w-[50%]' : ''}`}
            >
              <div className="flex flex-wrap items-center justify-end gap-2 mb-1">
                {exampleBadge}
                {heroProPill}
                {group.currency && (
                  <Badge
                    variant="info"
                    size="sm"
                    className="shrink-0 bg-black/70 dark:bg-black/70 text-white dark:text-white border-white/30 backdrop-blur-sm"
                  >
                    {group.currency}
                  </Badge>
                )}
                {group.userRole === 'owner' && (
                  <Badge
                    variant="warning"
                    size="sm"
                    className="shrink-0 bg-black/70 dark:bg-black/70 text-amber-300 dark:text-amber-300 backdrop-blur-sm"
                    aria-label="Owner"
                  >
                    <Crown className="h-3 w-3 mr-1" />
                    {t('members:roles.owner')}
                  </Badge>
                )}
                {group.userRole === 'admin' && (
                  <Badge
                    variant="info"
                    size="sm"
                    className="shrink-0 bg-black/70 dark:bg-black/70 text-blue-300 dark:text-blue-300 backdrop-blur-sm"
                    aria-label="Admin"
                  >
                    <ShieldCheckIcon className="h-3 w-3 mr-1" />
                    {t('members:roles.admin')}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {proAction(
                  'group-pro-action',
                  'bg-gray-800 hover:bg-gray-700 text-orange-400 hover:text-orange-300 whitespace-nowrap'
                )}
                {group.userRole !== 'owner' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={leaveGroup}
                    disabled={isLeaving}
                    data-testid="leave-group-button"
                    className="bg-gray-800 hover:bg-gray-700 text-red-400 hover:text-red-300 whitespace-nowrap"
                  >
                    {isLeaving ? (
                      <div className="loading-spinner w-4 h-4 mr-1 border-white"></div>
                    ) : (
                      <LogOutIcon className="w-4 h-4 mr-1" />
                    )}
                    {t('groups:leave.action')}
                  </Button>
                )}
                {canAccessSettings && (
                  <LinkButton
                    href={`/groups/${group.id}/edit`}
                    data-testid="group-settings-button"
                    className="!bg-gray-800 hover:!bg-gray-700 !text-orange-400 hover:!text-orange-300"
                  >
                    <SettingsIcon className="w-4 h-4 mr-1" />
                    {t('groups:settings.title')}
                  </LinkButton>
                )}
              </div>
            </div>
          </div>

          {/* Desktop: Stats at bottom */}
          <div className="hidden sm:block absolute bottom-0 left-0 right-0 p-6 z-10">
            <div className="flex flex-wrap items-center gap-2 text-sm text-white pr-48 caption-shadow">
              {dateDisplay ? (
                <>
                  <span className="flex items-center gap-1">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {dateDisplay}
                  </span>
                  <span>•</span>
                </>
              ) : (
                <>
                  <span>
                    {t('groups:stats.createdPrefix')}{' '}
                    <RelativeTime date={group.createdAt} />
                  </span>
                  <span>•</span>
                </>
              )}
              <Link
                to={`/groups/${group.id}/members`}
                className="hover:underline"
              >
                {membersLabel}
              </Link>
            </div>
          </div>

          {/* Desktop: Add Expense Button overlaid on hero */}
          <div className="hidden sm:block absolute bottom-6 right-6 z-20">
            <PreloadLink
              href={`/groups/${group.id}/expenses/create`}
              preloadType="create-expense"
            >
              <Button
                variant="primary"
                leftIcon={<PlusIcon className="w-5 h-5" />}
                data-testid="group-add-expense-button-desktop"
                title={`${t('groups:actions.addExpense')} (${modifierSymbol}+E)`}
                aria-label={t('groups:actions.addExpense')}
                aria-keyshortcuts={`${modifierSymbol}+E`}
                className="shadow-lg"
              >
                <span className="flex items-center gap-2">
                  {t('groups:actions.addExpense')}
                  <kbd className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-semibold text-gray-500 bg-white/90 border border-gray-300 rounded-sm">
                    {modifierSymbol}+E
                  </kbd>
                </span>
              </Button>
            </PreloadLink>
          </div>
        </div>

        {/* Description below hero (both mobile and desktop) */}
        {group.description && (
          <p className="mt-3 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {group.description}
          </p>
        )}

        {/* Mobile: Buttons below hero banner (same layout as non-hero version) */}
        <div className="sm:hidden mt-4">
          <div className="flex flex-col gap-2">
            <PreloadLink
              href={`/groups/${group.id}/expenses/create`}
              preloadType="create-expense"
              className="w-full"
            >
              <Button
                variant="primary"
                leftIcon={<PlusIcon className="w-5 h-5" />}
                className="w-full"
                data-testid="group-add-expense-button"
                title={`${t('groups:actions.addExpense')} (${modifierSymbol}+E)`}
                aria-label={t('groups:actions.addExpense')}
                aria-keyshortcuts={`${modifierSymbol}+E`}
              >
                {t('groups:actions.addExpense')}
              </Button>
            </PreloadLink>
            <div className="w-full flex flex-wrap gap-2 justify-center">
              {proAction('group-pro-action-mobile')}
              {group.userRole !== 'owner' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={leaveGroup}
                  disabled={isLeaving}
                  data-testid="leave-group-button-mobile"
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/10 whitespace-nowrap"
                >
                  {isLeaving ? (
                    <div className="loading-spinner w-4 h-4 mr-1 dark:border-red-400"></div>
                  ) : (
                    <LogOutIcon className="w-4 h-4 mr-1" />
                  )}
                  {t('groups:leave.action')}
                </Button>
              )}
              {canAccessSettings && (
                <LinkButton
                  href={`/groups/${group.id}/edit`}
                  data-testid="group-settings-button-mobile"
                >
                  <SettingsIcon className="w-4 h-4 mr-1" />
                  {t('groups:settings.title')}
                </LinkButton>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default header when no image - matches hero banner layout
  return (
    <div ref={headerRef} data-testid="group-info">
      {proDialog}
      {/* Mobile: Back button + Title + Badges at top */}
      <div className="sm:hidden">
        <div className="flex items-start gap-3">
          <BackButton
            variant="ghost"
            size="sm"
            className="p-2 shrink-0"
            fallbackUrl="/groups"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white line-clamp-3">
                {group.name}
              </h1>
              {exampleBadge}
              {proPill}
              {group.currency && (
                <Badge variant="info" size="sm" className="shrink-0">
                  {group.currency}
                </Badge>
              )}
              {group.userRole === 'owner' && (
                <Badge
                  variant="warning"
                  size="sm"
                  className="shrink-0"
                  aria-label="Owner"
                >
                  <Crown className="h-3 w-3 mr-1" />
                  {t('members:roles.owner')}
                </Badge>
              )}
              {group.userRole === 'admin' && (
                <Badge
                  variant="info"
                  size="sm"
                  className="shrink-0"
                  aria-label="Admin"
                >
                  <ShieldCheckIcon className="h-3 w-3 mr-1" />
                  {t('members:roles.admin')}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Mobile: Stats */}
        <div className="mt-2 ml-11 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          {dateDisplay ? (
            <>
              <span className="flex items-center gap-1">
                <CalendarIcon className="w-3.5 h-3.5" />
                {dateDisplay}
              </span>
              <span>•</span>
            </>
          ) : (
            <>
              <span>
                {t('groups:stats.createdPrefix')}{' '}
                <RelativeTime date={group.createdAt} />
              </span>
              <span>•</span>
            </>
          )}
          <Link to={`/groups/${group.id}/members`} className="hover:underline">
            {membersLabel}
          </Link>
        </div>
      </div>

      {/* Desktop: Back Button + Title on left, Badges + Settings on right */}
      <div className="hidden sm:flex justify-between items-start">
        <div className="flex items-start gap-3 flex-1 min-w-0 pr-4">
          <BackButton
            variant="ghost"
            size="sm"
            className="p-2 shrink-0"
            fallbackUrl="/groups"
          />
          <div className="flex-1 min-w-0 pt-0.5">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white line-clamp-3">
              {group.name}
            </h1>
            {/* Desktop: Stats */}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              {dateDisplay ? (
                <>
                  <span className="flex items-center gap-1">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {dateDisplay}
                  </span>
                  <span>•</span>
                </>
              ) : (
                <>
                  <span>
                    {t('groups:stats.createdPrefix')}{' '}
                    <RelativeTime date={group.createdAt} />
                  </span>
                  <span>•</span>
                </>
              )}
              <Link
                to={`/groups/${group.id}/members`}
                className="hover:underline"
              >
                {membersLabel}
              </Link>
            </div>
          </div>
        </div>
        <div
          className={`flex flex-col items-end gap-2 shrink-0${showProPill ? ' max-w-[50%]' : ''}`}
        >
          <div className="flex flex-wrap items-center justify-end gap-2 mb-1">
            {exampleBadge}
            {proPill}
            {group.currency && (
              <Badge variant="info" size="sm" className="shrink-0">
                {group.currency}
              </Badge>
            )}
            {group.userRole === 'owner' && (
              <Badge
                variant="warning"
                size="sm"
                className="shrink-0"
                aria-label="Owner"
              >
                <Crown className="h-3 w-3 mr-1" />
                {t('members:roles.owner')}
              </Badge>
            )}
            {group.userRole === 'admin' && (
              <Badge
                variant="info"
                size="sm"
                className="shrink-0"
                aria-label="Admin"
              >
                <ShieldCheckIcon className="h-3 w-3 mr-1" />
                {t('members:roles.admin')}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {proAction('group-pro-action')}
            {group.userRole !== 'owner' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={leaveGroup}
                disabled={isLeaving}
                data-testid="leave-group-button"
                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/10 whitespace-nowrap"
              >
                {isLeaving ? (
                  <div className="loading-spinner w-4 h-4 mr-1 dark:border-red-400"></div>
                ) : (
                  <LogOutIcon className="w-4 h-4 mr-1" />
                )}
                {t('groups:leave.action')}
              </Button>
            )}
            {(group.userRole === 'admin' || group.userRole === 'owner') && (
              <LinkButton
                href={`/groups/${group.id}/edit`}
                data-testid="group-settings-button"
              >
                <SettingsIcon className="w-4 h-4 mr-1" />
                {t('groups:settings.title')}
              </LinkButton>
            )}
          </div>
        </div>
      </div>

      {/* Description below header (both mobile and desktop) */}
      {group.description && (
        <p className="mt-3 ml-11 sm:ml-11 text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {group.description}
        </p>
      )}

      {/* Buttons below (same layout as hero version) */}
      <div className="mt-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <PreloadLink
            href={`/groups/${group.id}/expenses/create`}
            preloadType="create-expense"
            className="w-full sm:w-auto"
          >
            <Button
              variant="primary"
              leftIcon={<PlusIcon className="w-5 h-5" />}
              className="w-full sm:w-auto"
              data-testid="group-add-expense-button"
              title={`${t('groups:actions.addExpense')} (${modifierSymbol}+E)`}
              aria-label={t('groups:actions.addExpense')}
              aria-keyshortcuts={`${modifierSymbol}+E`}
            >
              <span className="flex items-center gap-2">
                {t('groups:actions.addExpense')}
                <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-sm">
                  {modifierSymbol}+E
                </kbd>
              </span>
            </Button>
          </PreloadLink>
          <div className="sm:hidden w-full flex flex-wrap gap-2 justify-center">
            {proAction('group-pro-action-mobile')}
            {group.userRole !== 'owner' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={leaveGroup}
                disabled={isLeaving}
                data-testid="leave-group-button-mobile"
                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/10 whitespace-nowrap"
              >
                {isLeaving ? (
                  <div className="loading-spinner w-4 h-4 mr-1 dark:border-red-400"></div>
                ) : (
                  <LogOutIcon className="w-4 h-4 mr-1" />
                )}
                {t('groups:leave.action')}
              </Button>
            )}
            {(group.userRole === 'admin' || group.userRole === 'owner') && (
              <LinkButton
                href={`/groups/${group.id}/edit`}
                data-testid="group-settings-button-mobile"
              >
                <SettingsIcon className="w-4 h-4 mr-1" />
                {t('groups:settings.title')}
              </LinkButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
